import { randomUUID } from "expo-crypto";
import { useCallback, useRef, useState } from "react";
import { Platform } from "react-native";
import * as WebBrowser from "expo-web-browser";
import { router } from "expo-router";
import {
  useMusicSync,
  useMusicSyncGetAuthUrl,
  musicSyncGetPlaylistSyncProgressQuery,
} from "@hooks/trpc/useMusicSync";
import { useData } from "../../runtime/provider";
import { captureException } from "@utils/captureException";
import { trackOnb } from "./analytics/trackOnboarding";
import { useOnboardingStore } from "./store/onboardingStore";
import type { StreamingService } from "./constants";
import {
  callbackDataFromUrl,
  classifyImportAuthError,
  importAuthMessages,
  parseImportCallback,
  type ImportAuthCode,
} from "./importAuth";
import {
  beginImportAttempt,
  readImportSession,
  saveImportSession,
  trackImportStep,
  viewImportSession,
  type ImportSession,
} from "./importSession";

// Expo Router can receive the deep link as well as the auth-session promise.
// Share the in-flight completion so the two paths cannot submit two imports.
type AuthorizedImport = {
  session: ImportSession;
  data: Extract<ReturnType<typeof parseImportCallback>, { ok: true }>;
};
type Completion =
  | { ok: true }
  | { ok: false; code: ImportAuthCode; authorized?: AuthorizedImport };
const completions = new Map<string, Promise<Completion>>();
let browserBusy = false;

export function useSetupMusicImport() {
  const dataSession = useData();
  const { queryClient } = dataSession;
  const { mutateAsync: getAuthUrl } = useMusicSyncGetAuthUrl();
  const { mutateAsync: syncMusic } = useMusicSync();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [selectedService, setSelectedService] = useState<StreamingService>();
  const view = useRef<Promise<ImportSession> | undefined>(undefined);
  const authorized = useRef<AuthorizedImport | undefined>(undefined);

  // Each mounted screen needs the shared result to render recovery and retry
  // with the same validated connection, even when another screen did the work.
  const observeCompletion = useCallback(
    async (session: ImportSession, work: Promise<Completion>) => {
      setBusy(true);
      setSelectedService(session.service);
      setMessage(undefined);
      try {
        const result = await work;
        if (!result.ok) {
          setMessage(importAuthMessages[result.code]);
          authorized.current = result.authorized;
        }
        return result.ok;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const initialize = useCallback(() => {
    view.current ??= viewImportSession();
    return view.current;
  }, []);

  const fail = useCallback(
    async (
      session: ImportSession,
      code: ImportAuthCode,
      stage: "oauth" | "import" = "oauth",
    ) => {
      setMessage(importAuthMessages[code]);
      setSelectedService(session.service);
      await saveImportSession({ ...session, status: "error" });
      trackImportStep(
        session,
        stage === "oauth" ? "oauth_result" : "import_start_failed",
        {
          outcome:
            code === "user_cancelled"
              ? "cancel"
              : code === "browser_dismissed"
                ? "unknown"
                : "error",
          error_code: code,
        },
      );
      trackOnb(
        "signup",
        stage === "oauth" ? "setup-oauth" : "bring-music",
        code === "user_cancelled" ? "cancel" : "error",
        { dsp: session.service, error_code: code },
      );
      if (
        !["user_cancelled", "browser_dismissed", "provider_denied"].includes(
          code,
        )
      ) {
        // Report only bounded codes; callback URLs/provider errors can contain credentials.
        captureException(new Error(`Playlist import: ${code}`), {
          channel: "error",
          area: "playlist-import",
          operation: stage,
          tags: {
            error_code: code,
            dsp: session.service ?? "unknown",
            attempt_id: session.attemptId ?? "unknown",
          },
        });
      }
    },
    [],
  );

  const accept = useCallback(
    async (
      session: ImportSession,
      data: Extract<ReturnType<typeof parseImportCallback>, { ok: true }>,
    ) => {
      const importAttemptId = session.attemptId ?? randomUUID();
      session = { ...session, attemptId: importAttemptId };
      await saveImportSession(session);
      authorized.current = { session, data };
      trackImportStep(session, "import_requested");
      try {
        const result = await syncMusic({
          excludedPlaylists: [],
          service: data.service,
          integrationUserUUID: data.integrationUserUUID,
          importAttemptId,
          importFunnelId: session.funnelId,
        });
        await saveImportSession({ ...session, status: "accepted" });
        trackImportStep(session, "import_accepted");
        trackOnb("signup", "bring-music", "import-started", {
          dsp: data.service,
          attempt_id: session.attemptId,
        });
        const store = useOnboardingStore.getState();
        store.setExcludedPlaylists([], data.service, data.integrationUserUUID);
        if (result.hasMatchedSongs) store.setHasImportedSongs(true);
        void queryClient
          .invalidateQueries({
            queryKey:
              musicSyncGetPlaylistSyncProgressQuery(dataSession).queryKey,
          })
          .catch(console.error);
        router.replace("/onboarding/notification");
        return true;
      } catch {
        await fail(session, "import_start_failed", "import");
        return false;
      }
    },
    [syncMusic, fail, dataSession, queryClient],
  );

  const finishCallback = useCallback(
    async (data64: string | undefined, suppliedSession?: ImportSession) => {
      const session = suppliedSession ?? (await readImportSession());
      if (!session?.attemptId || !session.service) return undefined;
      if (session.status === "accepted") return true;
      if (!session.openedAt || Date.now() - session.openedAt > 30 * 60 * 1000) {
        await fail(session, "session_expired");
        return false;
      }
      const data = parseImportCallback(data64, session.service);
      if (!data.ok) {
        await fail(session, data.code);
        return false;
      }
      // Only validated callbacks may share the import request. An unrelated
      // deep link must not suppress the provider's valid auth-session return.
      const existing = completions.get(session.attemptId);
      if (existing) return observeCompletion(session, existing);
      const work = (async (): Promise<Completion> => {
        trackImportStep(session, "oauth_result", { outcome: "success" });
        trackOnb("signup", "setup-oauth", "success", {
          dsp: session.service,
          attempt_id: session.attemptId,
        });
        return (await accept(session, data))
          ? { ok: true }
          : {
              ok: false,
              code: "import_start_failed",
              authorized: { session, data },
            };
      })();
      completions.set(session.attemptId, work);
      try {
        return await observeCompletion(session, work);
      } finally {
        completions.delete(session.attemptId);
      }
    },
    [accept, fail, observeCompletion],
  );

  const start = useCallback(
    async (service: StreamingService) => {
      if (browserBusy) return;
      browserBusy = true;
      setBusy(true);
      setMessage(undefined);
      setSelectedService(service);
      let session: ImportSession | undefined;
      let stage: "url" | "browser" = "url";
      try {
        // A retry after an import API failure keeps the validated connection.
        if (
          authorized.current?.data.service === service &&
          authorized.current.session.openedAt &&
          Date.now() - authorized.current.session.openedAt < 30 * 60 * 1000
        ) {
          await accept(authorized.current.session, authorized.current.data);
          return;
        }
        await initialize();
        const latest = (await readImportSession()) ?? (await initialize());
        session = await beginImportAttempt(latest, service);
        trackOnb("signup", "bring-music", "dsp-pressed", {
          dsp: service,
          attempt_id: session.attemptId,
        });
        const returnUrl = `${Platform.OS === "web" ? window.location.origin : "lsn:/"}/onboarding/callback`;
        const { authUrl } = await getAuthUrl({ service, returnUrl });
        session = { ...session, openedAt: Date.now() };
        await saveImportSession(session);
        trackImportStep(session, "oauth_open");
        trackOnb("signup", "setup-oauth", "open", {
          dsp: service,
          attempt_id: session.attemptId,
        });
        stage = "browser";
        if (Platform.OS === "web") {
          window.location.assign(authUrl);
          return;
        }
        const result = await WebBrowser.openAuthSessionAsync(
          authUrl,
          returnUrl,
        );
        // The router may already have accepted this callback.
        const current = await readImportSession();
        if (
          current?.attemptId === session.attemptId &&
          current?.status === "accepted"
        )
          return;
        const callbackWork = session.attemptId
          ? completions.get(session.attemptId)
          : undefined;
        if (callbackWork) {
          await observeCompletion(session, callbackWork);
          return;
        }
        if (result.type === "success") {
          await finishCallback(
            callbackDataFromUrl(result.url, returnUrl),
            session,
          );
        } else {
          await fail(
            session,
            result.type === "cancel"
              ? "user_cancelled"
              : result.type === "dismiss"
                ? "browser_dismissed"
                : "browser_failed",
          );
        }
      } catch (error) {
        if (session) {
          const classified = classifyImportAuthError(error);
          await fail(
            session,
            stage === "url"
              ? "auth_url_failed"
              : classified === "provider_error"
                ? "browser_failed"
                : classified,
          );
        }
      } finally {
        browserBusy = false;
        setBusy(false);
      }
    },
    [accept, fail, finishCallback, getAuthUrl, initialize, observeCompletion],
  );

  return { initialize, start, finishCallback, busy, message, selectedService };
}
